import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';
import BookingCard from './BookingCard';
import ReceiptModal from './ReceiptModal';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PendingBookings = ({ salonId }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // States for Receipt Modal
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchPendingBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      // دریافت رزروهایی که وضعیت آنها pending یا review است
      const response = await api.get(`/salons/${salonId}/bookings?status=pending,review`);
      setBookings(response.data);
    } catch (err) {
      console.error('Error fetching pending bookings:', err);
      setError('خطا در دریافت رزروهای در انتظار. لطفا مجددا تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (salonId) {
      fetchPendingBookings();
    }
  }, [salonId]);

  const handleApprove = async (bookingId) => {
    try {
      setBookings(prev => prev.filter(b => b._id !== bookingId));
      await api.patch(`/salons/${salonId}/bookings/${bookingId}/updatestatus`, {
        status: 'confirmed'
      });
    } catch (err) {
      console.error('Error confirming booking:', err);
      fetchPendingBookings();
      alert('خطا در تایید رزرو رخ داد.');
    }
  };

  const handleReject = async (bookingId) => {
    try {
      setBookings(prev => prev.filter(b => b._id !== bookingId));
      await api.patch(`/salons/${salonId}/bookings/${bookingId}/updatestatus`, {
        status: 'cancelled'
      });
    } catch (err) {
      console.error('Error rejecting booking:', err);
      fetchPendingBookings();
      alert('خطا در لغو رزرو رخ داد.');
    }
  };

  const openReceiptModal = (booking) => {
    setSelectedBooking(booking);
    setModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        <span className="text-muted-foreground text-sm">در حال بارگذاری رزروهای در انتظار...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error/10 border border-error/20 p-4 rounded-xl text-error flex flex-col items-center gap-3 text-center" dir="rtl">
        <Icon name="AlertTriangle" size={24} />
        <span>{error}</span>
        <Button variant="outline" size="sm" onClick={fetchPendingBookings}>تلاش مجدد</Button>
      </div>
    );
  }

  // تفکیک نوبت‌ها به در انتظار پرداخت و در انتظار بررسی رسید
  const reviewBookings = bookings.filter(b => b.status === 'review');
  const justPendingBookings = bookings.filter(b => b.status === 'pending');

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground">رزروهای نیازمند اقدام سریع</h2>
        <Button variant="ghost" size="sm" iconName="RefreshCw" onClick={fetchPendingBookings}>
          بروزرسانی
        </Button>
      </div>

      {bookings.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center flex flex-col items-center justify-center gap-2 text-muted-foreground">
          <Icon name="CheckCircle2" size={40} className="text-success" />
          <span className="font-medium text-foreground">هیچ موردی نیست!</span>
          <span className="text-sm">تمامی رزروها تعیین تکلیف شده‌اند.</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* بخش رسیدهای فرستاده شده که اولویت بیشتری دارند */}
          {reviewBookings.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5 text-blue-500">
                <Icon name="Clock" size={16} />
                <span>در انتظار بررسی رسید ({reviewBookings.length})</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {reviewBookings.map((booking) => (
                  <BookingCard
                    key={booking._id}
                    booking={booking}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onViewReceipt={openReceiptModal}
                    showActions={true}
                  />
                ))}
              </div>
            </div>
          )}

          {/* بخش رزروهایی که هنوز کاربر برایشان رسید نفرستاده است */}
          {justPendingBookings.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5 text-warning">
                <Icon name="Clock" size={16} />
                <span>در انتظار پرداخت بیعانه ({justPendingBookings.length})</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {justPendingBookings.map((booking) => (
                  <BookingCard
                    key={booking._id}
                    booking={booking}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    showActions={true}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Receipt Modal */}
      <ReceiptModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        receiptUrl={selectedBooking?.receiptUrl}
        booking={selectedBooking}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  );
};

export default PendingBookings;
